import { useState, useEffect } from 'react';
import { Note, CreateNoteData, UpdateNoteData } from '../types/notes';
import { notesService } from '../services/notesService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useNotes = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Jegyzetek betöltése
  const loadNotes = async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      setError(null);
      const fetchedNotes = await notesService.getNotes(user.uid);
      setNotes(fetchedNotes);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzetek betöltésekor';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Új jegyzet létrehozása
  const createNote = async (noteData: CreateNoteData): Promise<Note | null> => {
    if (!user?.uid) return null;

    try {
      const newNote = await notesService.createNote(user.uid, noteData);
      setNotes(prev => [newNote, ...prev]);
      toast.success('Jegyzet létrehozva');
      return newNote;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet létrehozásakor';
      toast.error(errorMessage);
      return null;
    }
  };

  // Jegyzet frissítése
  const updateNote = async (noteId: string, updates: UpdateNoteData): Promise<boolean> => {
    try {
      await notesService.updateNote(noteId, updates);
      setNotes(prev => prev.map(note => 
        note.id === noteId 
          ? { ...note, ...updates, updatedAt: new Date().toISOString() }
          : note
      ));
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet frissítésekor';
      toast.error(errorMessage);
      return false;
    }
  };

  // Jegyzet törlése
  const deleteNote = async (noteId: string): Promise<boolean> => {
    try {
      await notesService.deleteNote(noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      toast.success('Jegyzet törölve');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet törlésekor';
      toast.error(errorMessage);
      return false;
    }
  };

  // Jegyzet pozíciójának frissítése
  const updateNotePosition = async (noteId: string, position: { x: number; y: number }): Promise<void> => {
    // Optimista frissítés - azonnal frissítjük a UI-t
    setNotes(prev => prev.map(note => 
      note.id === noteId 
        ? { ...note, position }
        : note
    ));

    // Háttérben mentjük az adatbázisba
    try {
      await notesService.updateNote(noteId, { position });
    } catch (err) {
      // Ha hiba van, visszaállítjuk az eredeti pozíciót
      loadNotes();
      console.error('Error updating note position:', err);
    }
  };

  // Jegyzet archiválása
  const archiveNote = async (noteId: string): Promise<boolean> => {
    try {
      await notesService.archiveNote(noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      toast.success('Jegyzet archiválva');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Hiba történt a jegyzet archiválásakor';
      toast.error(errorMessage);
      return false;
    }
  };

  // Komponens mount-kor betöltjük a jegyzeteket
  useEffect(() => {
    loadNotes();
  }, [user?.uid]);

  return {
    notes,
    loading,
    error,
    createNote,
    updateNote,
    deleteNote,
    updateNotePosition,
    archiveNote,
    refreshNotes: loadNotes
  };
};
